import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardFooter,
  CardHeader,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Media,
  Modal,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
} from 'reactstrap';
import Header from 'components/Headers/Header.js';
import { axiosGet, axiosPost } from 'util/apiConfig.js';

const STATUSES = ['pending', 'contacted', 'enrolled', 'closed'];
const STATUS_COLOR = { pending: 'warning', contacted: 'info', enrolled: 'success', closed: 'secondary' };

const EMPTY_LEAD = {
  friendName: '',
  friendPhone: '',
  friendEmail: '',
  referrerName: '',
  referrerEmail: '',
  referrerUserId: '',
  status: 'pending',
  notes: '',
};

const Referrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [lead, setLead] = useState(EMPTY_LEAD);

  const [linkTarget, setLinkTarget] = useState(null); // referral being linked
  const [linkForm, setLinkForm] = useState({ referrerUserId: '', referrerName: '', referrerEmail: '' });
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const limit = 10;

  const openLink = (r) => {
    setLinkTarget(r);
    setLinkForm({ referrerUserId: '', referrerName: '', referrerEmail: '' });
    setLinkSearch('');
    setLinkResults([]);
  };

  const closeLink = () => {
    setLinkTarget(null);
    setLinkForm({ referrerUserId: '', referrerName: '', referrerEmail: '' });
    setLinkSearch('');
    setLinkResults([]);
  };

  const pickUser = (u) =>
    setLinkForm({ referrerUserId: u.userId, referrerName: u.name || '', referrerEmail: u.email || '' });

  const fetchReferrals = useCallback(() => {
    setLoading(true);
    const q = statusFilter ? `?status=${statusFilter}` : '';
    axiosGet(`referral/list/${page}/${limit}${q}`)
      .then((res) => {
        if (!res.isError && res.data) {
          setReferrals(res.data.results || []);
          setTotalPage(res.data.totalPage || 1);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  // Debounced user search for the "Link user" picker.
  useEffect(() => {
    if (!linkTarget) return undefined;
    const term = linkSearch.trim();
    if (!term) {
      setLinkResults([]);
      return undefined;
    }
    const t = setTimeout(() => {
      setSearchingUsers(true);
      axiosGet(`referral/search-users?q=${encodeURIComponent(term)}`)
        .then((res) => {
          if (!res.isError) setLinkResults(res.data || []);
        })
        .catch(() => {})
        .finally(() => setSearchingUsers(false));
    }, 300);
    return () => clearTimeout(t);
  }, [linkSearch, linkTarget]);

  const changeStatus = (referral, status) => {
    axiosPost('referral/update-status', { referralId: referral._id, status })
      .then((res) => {
        if (!res.isError) fetchReferrals();
      })
      .catch(() => {});
  };

  const submitLead = () => {
    if (!lead.friendName.trim() || !lead.friendPhone.trim()) return;
    axiosPost('referral/admin/add', {
      ...lead,
      friendName: lead.friendName.trim(),
      friendPhone: lead.friendPhone.trim(),
    })
      .then((res) => {
        if (!res.isError) {
          setAddOpen(false);
          setLead(EMPTY_LEAD);
          setPage(1);
          fetchReferrals();
        }
      })
      .catch(() => {});
  };

  const submitLink = () => {
    if (!linkForm.referrerUserId.trim()) return;
    axiosPost('referral/link-user', {
      referralId: linkTarget._id,
      referrerUserId: linkForm.referrerUserId.trim(),
      referrerName: linkForm.referrerName.trim(),
      referrerEmail: linkForm.referrerEmail.trim(),
      force: true,
    })
      .then((res) => {
        if (!res.isError) {
          closeLink();
          fetchReferrals();
        }
      })
      .catch(() => {});
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div style={{ maxWidth: 220 }}>
            <Input
              type="select"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Input>
          </div>
          <Button color="primary" onClick={() => setAddOpen(true)}>
            Add lead
          </Button>
        </div>

        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Referrals</h3>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Friend</th>
                    <th scope="col">Referred by</th>
                    <th scope="col">Source</th>
                    <th scope="col">Added by</th>
                    <th scope="col">Status</th>
                    <th scope="col">Date</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => (
                    <tr key={r._id}>
                      <th scope="row">
                        <Media className="align-items-center">
                          <Media>
                            <span className="mb-0 text-sm font-weight-bold">{r.friendName}</span>
                            <br />
                            <span className="text-muted text-xs">{r.friendPhone}</span>
                            {r.friendEmail ? (
                              <>
                                <br />
                                <span className="text-muted text-xs">{r.friendEmail}</span>
                              </>
                            ) : null}
                          </Media>
                        </Media>
                      </th>
                      <td>
                        {r.referrerUserId || r.referrerName ? (
                          <>
                            <span className="text-sm">{r.referrerName || '(unnamed)'}</span>
                            {r.referrerEmail ? (
                              <>
                                <br />
                                <span className="text-muted text-xs">{r.referrerEmail}</span>
                              </>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-muted text-xs">Not linked</span>
                        )}
                      </td>
                      <td>
                        <Badge color={r.source === 'admin' ? 'default' : 'primary'} pill>
                          {r.source}
                        </Badge>
                      </td>
                      <td>
                        <span className="text-sm">{r.addedByName || '—'}</span>
                      </td>
                      <td style={{ minWidth: 140 }}>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={r.status}
                          onChange={(e) => changeStatus(r, e.target.value)}
                          className={`text-${STATUS_COLOR[r.status] || 'dark'}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Input>
                      </td>
                      <td>{fmtDate(r.createdAt)}</td>
                      <td>
                        {!r.referrerUserId ? (
                          <Button size="sm" color="secondary" outline onClick={() => openLink(r)}>
                            Link user
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {!loading && referrals.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No referrals yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <nav aria-label="pagination">
                  <Pagination className="pagination justify-content-end mb-0">
                    <PaginationItem disabled={page <= 1}>
                      <PaginationLink onClick={() => setPage(page - 1)} tabIndex="-1">
                        Prev
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled>
                      <PaginationLink>
                        {page} / {totalPage}
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={page >= totalPage}>
                      <PaginationLink onClick={() => setPage(page + 1)}>Next</PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </nav>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Add lead modal */}
      <Modal className="modal-dialog-centered" isOpen={addOpen} toggle={() => setAddOpen(false)}>
        <div className="modal-header">
          <h5 className="modal-title">Add referral lead</h5>
          <button type="button" className="close" onClick={() => setAddOpen(false)}>
            <span aria-hidden>×</span>
          </button>
        </div>
        <div className="modal-body">
          <Form>
            <FormGroup>
              <Label>Friend&apos;s name *</Label>
              <Input value={lead.friendName} onChange={(e) => setLead({ ...lead, friendName: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Phone (WhatsApp) *</Label>
              <Input value={lead.friendPhone} onChange={(e) => setLead({ ...lead, friendPhone: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Friend email</Label>
              <Input value={lead.friendEmail} onChange={(e) => setLead({ ...lead, friendEmail: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Referred by — name</Label>
              <Input value={lead.referrerName} onChange={(e) => setLead({ ...lead, referrerName: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Referred by — email</Label>
              <Input value={lead.referrerEmail} onChange={(e) => setLead({ ...lead, referrerEmail: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Referred by — user id (optional)</Label>
              <Input
                value={lead.referrerUserId}
                onChange={(e) => setLead({ ...lead, referrerUserId: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input type="select" value={lead.status} onChange={(e) => setLead({ ...lead, status: e.target.value })}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Notes</Label>
              <Input type="textarea" value={lead.notes} onChange={(e) => setLead({ ...lead, notes: e.target.value })} />
            </FormGroup>
          </Form>
        </div>
        <div className="modal-footer">
          <Button color="link" onClick={() => setAddOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={submitLead} disabled={!lead.friendName.trim() || !lead.friendPhone.trim()}>
            Save lead
          </Button>
        </div>
      </Modal>

      {/* Link user modal */}
      <Modal className="modal-dialog-centered" isOpen={!!linkTarget} toggle={closeLink}>
        <div className="modal-header">
          <h5 className="modal-title">Link a user to this referral</h5>
          <button type="button" className="close" onClick={closeLink}>
            <span aria-hidden>×</span>
          </button>
        </div>
        <div className="modal-body">
          <Form>
            <FormGroup>
              <Label>Search by name, email or phone</Label>
              <Input
                placeholder="Start typing a name or email…"
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
                autoFocus
              />
            </FormGroup>

            {searchingUsers ? <p className="text-muted text-sm mb-2">Searching…</p> : null}
            {!searchingUsers && linkSearch.trim() && linkResults.length === 0 ? (
              <p className="text-muted text-sm mb-2">No matching users.</p>
            ) : null}

            {linkResults.length > 0 ? (
              <div className="list-group mb-3" style={{ maxHeight: 220, overflowY: 'auto' }}>
                {linkResults.map((u) => {
                  const selected = linkForm.referrerUserId === u.userId;
                  return (
                    <button
                      type="button"
                      key={u.userId}
                      className={`list-group-item list-group-item-action ${selected ? 'active' : ''}`}
                      onClick={() => pickUser(u)}
                    >
                      <span className="font-weight-bold">{u.name || '(unnamed)'}</span>
                      {u.email ? <span className="text-sm"> · {u.email}</span> : null}
                      {u.mobileNumber ? <span className="text-sm"> · {u.mobileNumber}</span> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <hr className="my-3" />
            <p className="text-muted text-xs mb-2">Selected user (pick above, or enter manually):</p>
            <FormGroup>
              <Label>User id *</Label>
              <Input
                value={linkForm.referrerUserId}
                onChange={(e) => setLinkForm({ ...linkForm, referrerUserId: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Name</Label>
              <Input
                value={linkForm.referrerName}
                onChange={(e) => setLinkForm({ ...linkForm, referrerName: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                value={linkForm.referrerEmail}
                onChange={(e) => setLinkForm({ ...linkForm, referrerEmail: e.target.value })}
              />
            </FormGroup>
          </Form>
        </div>
        <div className="modal-footer">
          <Button color="link" onClick={closeLink}>
            Cancel
          </Button>
          <Button color="primary" onClick={submitLink} disabled={!linkForm.referrerUserId.trim()}>
            Link user
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Referrals;
